/**
 * CustomerLoginPage — citizen sign-in.
 *
 * Accepts an "Email or phone" identifier (single field). The backend
 * `service-identity` `/v1/auth/login` route is identifier-agnostic — it
 * resolves email vs phone on the `@` heuristic — so we just normalise
 * NG-local phone inputs to E.164 before posting, leave emails as-is, and
 * leave anything else (admin staff signing in with their own email forms)
 * to the server.
 *
 * Both phone and email are unique on the identity DB (commit ee1c78e3),
 * so either resolves to at most one user account.
 */
import { type FormEvent, type JSX, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, AtSign, Lock, Loader2 } from 'lucide-react';

import { setCustomerRefreshToken, setCustomerToken } from '../../lib/auth/tokenStore';
import { env } from '../../lib/env';

interface Touched {
  readonly identifier: boolean;
  readonly password: boolean;
}

/**
 * Normalize whatever the user typed into the canonical form the backend
 * expects. The backend's `login()` already handles email-normalisation
 * (lower + trim) and phone E.164 itself, but the more we send in the
 * canonical form the cleaner our access logs are.
 */
function normaliseIdentifier(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.includes('@')) return trimmed.toLowerCase();
  // Phone path: strip non-digit-or-plus chars, then map NG locals to +234.
  const cleaned = trimmed.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  if (cleaned.startsWith('0')) return `+234${cleaned.slice(1)}`;
  if (cleaned.startsWith('234')) return `+${cleaned}`;
  // Bare NG local (10 digits, e.g. 8012345678): assume NG.
  if (/^\d{10}$/.test(cleaned)) return `+234${cleaned}`;
  return cleaned;
}

function isLikelyEmail(raw: string): boolean {
  return raw.includes('@');
}

function isValidIdentifier(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return false;
  if (isLikelyEmail(trimmed)) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  }
  // Phone: accept any of +234..., 0..., or bare 10-11 digits.
  const digits = trimmed.replace(/[^\d]/g, '');
  return /^\d{10,14}$/.test(digits);
}

export function CustomerLoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Allow callers (RegisterPage's "already registered, sign in" CTA) to
  // pre-seed via ?phone= or ?email= so the user does not retype.
  const seededEmail = searchParams.get('email')?.trim() ?? '';
  const seededPhoneRaw = (searchParams.get('phone') ?? '').replace(/\D/g, '');
  const seededPhone = seededPhoneRaw.startsWith('234') ? seededPhoneRaw.slice(3) : seededPhoneRaw;
  const initialIdentifier = seededEmail.length > 0 ? seededEmail : seededPhone;
  const [identifier, setIdentifier] = useState<string>(initialIdentifier);
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [touched, setTouched] = useState<Touched>({ identifier: false, password: false });

  const identifierValid = isValidIdentifier(identifier);
  const passwordValid = password.length >= 4;

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setTouched({ identifier: true, password: true });
    setError('');

    if (!identifierValid || !passwordValid) return;

    setLoading(true);
    try {
      const normalised = normaliseIdentifier(identifier);
      const res = await fetch(`${env.identityBaseUrl}/v1/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({ identifier: normalised, password }),
      });
      if (res.status === 401) {
        let detail: string | undefined;
        try {
          const body = (await res.json()) as { detail?: unknown };
          if (typeof body?.detail === 'string') detail = body.detail;
        } catch { /* ignore non-JSON */ }
        if (detail === 'ACCOUNT_LOCKED') {
          setError('Your account is temporarily locked after too many sign-in attempts. Please try again later.');
        } else {
          setError('Email/phone or password is incorrect. Please try again.');
        }
        return;
      }
      if (res.status >= 500) {
        setError('We are having trouble signing you in right now. Please try again in a moment.');
        return;
      }
      if (!res.ok) {
        setError('Sign-in failed. Please check your details and try again.');
        return;
      }
      const tokens = (await res.json()) as {
        accessToken?: string;
        refreshToken?: string;
        expiresIn?: number;
      };
      if (!tokens.accessToken || !tokens.refreshToken) {
        setError('Sign-in returned a malformed response. Try again.');
        return;
      }
      setCustomerToken(tokens.accessToken);
      setCustomerRefreshToken(tokens.refreshToken);
      navigate('/portal/dashboard', { replace: true });
    } catch {
      setError('Could not reach the sign-in service. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  const identifierKind = isLikelyEmail(identifier) ? 'email' : 'phone';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-nipost-green flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-white font-bold text-2xl">N</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">UPMP</h1>
          <p className="text-muted text-sm mt-1">Customer Portal</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-border p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Customer Sign In</h2>
          <p className="text-muted text-sm mb-6">Access your postal services account</p>

          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="login-identifier" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email or phone
              </label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="login-identifier"
                  name="identifier"
                  type="text"
                  inputMode={identifierKind === 'email' ? 'email' : 'tel'}
                  autoComplete="username"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, identifier: true }))}
                  placeholder="you@example.com or 08012345678"
                  className={`w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm transition-colors outline-none focus:ring-2 focus:ring-nipost-green/20 ${
                    touched.identifier && !identifierValid
                      ? 'border-red-400 bg-red-50/50'
                      : touched.identifier && identifierValid
                      ? 'border-green-400'
                      : 'border-border'
                  } focus:border-nipost-green`}
                />
              </div>
              {touched.identifier && !identifierValid && identifier.length > 0 && (
                <p className="text-red-500 text-xs mt-1">
                  Enter your registered email address or phone number.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                  placeholder="Enter your password"
                  className={`w-full pl-10 pr-11 py-2.5 rounded-lg border text-sm transition-colors outline-none focus:ring-2 focus:ring-nipost-green/20 ${
                    touched.password && !passwordValid
                      ? 'border-red-400 bg-red-50/50'
                      : touched.password && passwordValid
                      ? 'border-green-400'
                      : 'border-border'
                  } focus:border-nipost-green`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {touched.password && !passwordValid && password.length > 0 && (
                <p className="text-red-500 text-xs mt-1">Password must be at least 4 characters</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-5 flex items-center justify-between text-sm">
            <Link
              to="/auth/forgot-password"
              className="text-nipost-green hover:text-nipost-green-dark font-medium"
            >
              Forgot Password?
            </Link>
            <Link
              to="/auth/register"
              className="text-nipost-green hover:text-nipost-green-dark font-medium"
            >
              Create Account
            </Link>
          </div>
        </div>

        
      </div>
    </div>
  );
}
